// src/padplanner/editorPalette.js
//
// Which template sets the plan editor offers (S4a's rule, D-S4-1, which 151's
// guard enforces): the plan's pinned set, and any published AI PRIME
// standard set. Nothing else can be placed, so nothing else is shown.
//
// AN UNPINNED PLAN (created before its org published a library) shows the
// standard sets only. It can be pinned ONCE, to a published set of its own
// org (PUT /plans/:id/template-set, D-S4-2); the standard set needs no pin.
// After that the pin is final, and the confirm says so.
//
// Only footprints and zones are placed from a template; lines are drawn
// (S4a: a line's geometry is the client's), and patterns are cut.

export const PLACEABLE_KINDS = Object.freeze(["footprint", "zone"]);

export const PIN_FINAL_TEXT = "A plan's template library is final once pinned: it can't be changed or removed.";
export const NO_OWN_LIBRARY_TEXT = "Your organization has no published template library yet. The AI PRIME standard library is available without one.";

const newestFirst = (a, b) => String(b.published_at ?? "").localeCompare(String(a.published_at ?? ""));

/**
 * @param {object} plan  the plan (plan.template_set is the pin, or null)
 * @param {object[]} sets  listTemplateSets().sets
 * @returns {{chips: {id, name, version, standard, retired}[], choosable: object[], canChoose: boolean}}
 */
export function paletteSets(plan, sets = []) {
  const pinned = plan?.template_set ?? null;
  const standards = sets.filter((s) => s.standard && s.status === "published").sort(newestFirst);
  const chips = [];
  if (pinned) {
    chips.push({ id: pinned.id, name: pinned.name, version: pinned.version, standard: pinned.standard === true, retired: pinned.status === "retired" });
  }
  for (const s of standards) {
    if (s.id !== pinned?.id) chips.push({ id: s.id, name: s.name, version: s.version, standard: true, retired: false });
  }
  const choosable = pinned ? [] : sets.filter((s) => !s.standard && s.status === "published").sort(newestFirst);
  return { chips, choosable, canChoose: !pinned };
}

/**
 * The sets whose templates the editor needs in order to draw what is already
 * placed: the palette's, plus every non-draft standard set (a feature may
 * come from a standard set since retired, which the server still accepts).
 */
export function templateSourceIds(plan, sets = []) {
  const ids = new Set(paletteSets(plan, sets).chips.map((c) => c.id));
  for (const s of sets) if (s.standard && s.status !== "draft") ids.add(s.id);
  return [...ids];
}

export const placeable = (templates) => (templates ?? []).filter((t) => PLACEABLE_KINDS.includes(t.kind));
